import { RefObject, useCallback, useEffect } from 'react';

import { AgGridReact } from 'ag-grid-react';

import { SelectionService } from '../../utils/selectionService';

type FertEmbryoData = {
  fertId: string;
  tempEmbryos?: Array<unknown>;
  liveEmbryos?: Array<unknown>;
};

type UseGridSelectionProps = {
  gridRef: RefObject<AgGridReact>;
  allLiveEmbryosSelected: boolean;
  allTempEmbryosSelected: boolean;
  fertEmbryosData?: FertEmbryoData[];
};

export const useGridSelection = ({
  gridRef,
  allLiveEmbryosSelected,
  allTempEmbryosSelected,
  fertEmbryosData,
}: UseGridSelectionProps) => {
  const updateGridSelection = useCallback(() => {
    if (!gridRef.current?.api) return;

    const api = gridRef.current.api;

    if (allLiveEmbryosSelected === false && allTempEmbryosSelected === false) {
      api.deselectAll();
      SelectionService.updateSelection('fert', []);
      return;
    }

    if (allTempEmbryosSelected || allLiveEmbryosSelected) {
      api.forEachNode((node) => {
        const fertData = fertEmbryosData?.find(
          (fert) => fert.fertId === node.data.fertId,
        );

        const hasTempEmbryos = (fertData?.tempEmbryos?.length as number) > 0;
        const hasLiveEmbryos = (fertData?.liveEmbryos?.length as number) > 0;

        const shouldSelect =
          (allTempEmbryosSelected && hasTempEmbryos) ||
          (allLiveEmbryosSelected && hasLiveEmbryos);

        node.setSelected(shouldSelect);
      });
      return;
    }

    const selection = SelectionService.getSelection();
    api.forEachNode((node) => {
      const isSelected = selection.fertilizationIds.includes(node.data.fertId);
      node.setSelected(isSelected);
    });
  }, [
    gridRef,
    allLiveEmbryosSelected,
    allTempEmbryosSelected,
    fertEmbryosData,
  ]);

  useEffect(() => {
    updateGridSelection();
  }, [updateGridSelection]);

  return {
    updateGridSelection,
  };
};
